import { DeliveredStatus } from '@amzn/aet-coachingform-kit/lib/types';
import {
  DisplayOrderForVertical,
  VerticalOption,
} from '@amzn/aet-galactus-kit';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Pagination from '@amzn/meridian/pagination';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { DisplayBenchmark } from './DisplayBenchmark';

import DataSource from '../../../../api';
import {
  EmptyHMDGoal,
  EmptyTeam,
  EmptyTeamProfile,
  Metric,
  Profile,
  ReportingTeamUser,
  Team as TeamType,
  TeamProfile,
  User,
  WeeklySummaryCoachingStatus,
} from '../../../../Types';
import CoachingNote from '../../../../Types/CoachingNote';
import DataState from '../../../../Types/DataState';
import { ProxyRelatedUser } from '../../../../Types/ProxyUser';
import { displayPrr, displayRap, getCurrentWeek } from '../../../../Util';
import {
  DATE_SORT_DIRECTION,
  dateComprarator,
  daysInThePast,
} from '../../../../Util/DateHelper';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';
import { renderUrl, urls } from '../../../../config/Url';
import useCustomTracking from '../../../../Util/useCustomTracking';
import {
  parseStringInOrder,
  extractMetricValue,
  metricComprarator,
  metricCompraratorBenchmark,
  TeamTableSortMethod,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import {
  GeneralMetricType,
  LighthouseMetricOption,
  LighthouseMetrics,
  TimeOption,
  TRAILING_DAYS,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/types';
import TeamPermissions from '../../TeamPermissions';
import { DELIVERY_STATUS_LABEL } from './displayTypes';
import { CONSUMER_VERTICALS, D2_VERTICALS } from '../../../../Util/Vertical';
import { getMetricStringIdAndDefaultMessage } from './helper';
import { NavigateFunction, useNavigate } from 'react-router';

const DEFAULT_ITEM_COUNT_PER_PAGE = 30;
const LIGHTHOUSE_METRICS = 'lighthouseMetrics';

interface DirectReportsProps {
  cscEntityPrefix: string;
  vertical: VerticalOption;
  channelValue: string;
  skillValue: string;
  segmentValue: string;
  marketplaceValue: string;
  keys: string[]; //From Goa Team API, keep this because it contains "CoachingNote" field which is currently used to as the checking to show "Open" button in each CSA entry
  week: string;
  profile: TeamProfile | Profile;
  weeklySummaryCoachingStatus: Array<WeeklySummaryCoachingStatus>;
  showWeeklyCoachingStatus: boolean;
  teamLHMetrics: LighthouseMetrics[];
  achtEnabled: boolean; // For Go users (CSA/TM) in EU, ACHT is blocked.
  concessionEnabled: boolean;
  manageCoachingNotes: boolean;
  proxyCoachID?: string;
  sortingMethod: TeamTableSortMethod;
  sourcePage: string;
  teamMetricTypes: Set<string>;
}

export interface InvdividualMetricEntry {
  userId: string;
  displayName: string;
  alias: string;
  lighthouseMetrics?: LighthouseMetrics;
  isTm: boolean;
  lastCoachedDate?: moment.Moment;
  lastCoachingStatus?: DeliveredStatus;
}

interface SortParams {
  sortColumn: string;
  sortDirection: string;
}

const isMetricRelatedColumn = (sortColumn: string) =>
  sortColumn.startsWith(LIGHTHOUSE_METRICS);

const extractMetricOptionFromColumn = (sortColumn: string) =>
  sortColumn.substring(LIGHTHOUSE_METRICS.length);

const sortingFunction =
  (
    sortColumn: string,
    sortDirection: string,
    sortingMethod: TeamTableSortMethod
  ) =>
  (valueA: any, valueB: any): number => {
    if (isMetricRelatedColumn(sortColumn)) {
      const metricOption = extractMetricOptionFromColumn(sortColumn);
      if (sortingMethod === TeamTableSortMethod.METRIC_VALUE) {
        return metricComprarator(
          valueA[LIGHTHOUSE_METRICS],
          valueB[LIGHTHOUSE_METRICS],
          metricOption,
          sortDirection
        );
      } else {
        return metricCompraratorBenchmark(
          valueA[LIGHTHOUSE_METRICS],
          valueB[LIGHTHOUSE_METRICS],
          metricOption,
          sortDirection
        );
      }
    }
    const a =
      sortColumn === 'displayName'
        ? valueA[sortColumn].toLowerCase()
        : valueA[sortColumn];
    const b =
      sortColumn === 'displayName'
        ? valueB[sortColumn].toLowerCase()
        : valueB[sortColumn];

    if (sortColumn === 'displayName') {
      if (a < b) {
        return sortDirection === 'ascending' ? -1 : 1;
      }

      if (a > b) {
        return sortDirection === 'ascending' ? 1 : -1;
      }
      return 0;
    }
    if (sortColumn === 'lastCoachedDate') {
      return sortDirection === 'ascending'
        ? dateComprarator(a, b, DATE_SORT_DIRECTION.NULL_PAST)
        : dateComprarator(b, a, DATE_SORT_DIRECTION.NULL_PAST);
    }
    return sortDirection === 'ascending' ? a - b : b - a;
  };

const parseValues = (
  reports: any,
  weeklySummaryCoachingStatus: Array<any>
): any =>
  reports.map((report: any) => {
    const coachingStatus = weeklySummaryCoachingStatus?.find(
      coaching => coaching.userID === report.user.userId
    );
    return {
      userId: report.user.userId,
      displayName: report.user.displayName,
      alias: report.user.alias,
      prr: displayPrr(report.metrics),
      rap: displayRap(report.metrics),
      tm: report.tm || false,
      lastCoachedDate:
        coachingStatus && moment.utc(coachingStatus.lastCoachedDate).isValid()
          ? moment.utc(coachingStatus.lastCoachedDate)
          : undefined,
      lastCoachingStatus: coachingStatus?.lastCoachingStatus,
    };
  });

const constructProfileMap = (
  profile: any,
  proxyCoachID?: string
): Map<any, any> => {
  let profileMap = new Map();
  //Profile
  if (!profile.reportingTeam) {
    const filterTM = profile.proxyCoachees.filter(
      (proxyCoachee: ProxyRelatedUser) =>
        proxyCoachee.tmEntity.userID === proxyCoachID
    );

    if (filterTM) {
      filterTM[0].sharedReports.forEach((report: User) => {
        profileMap.set(report.userID, report);
      });
    }
  }
  // TeamProfile
  else {
    profile.reportingTeam.forEach((member: ReportingTeamUser) => {
      profileMap.set(member.relatedUser.userID, member);
    });
  }
  return profileMap;
};

export const parseIndividualValues = (
  teamLHMetrics: LighthouseMetrics[],
  profile: any,
  weeklySummaryCoachingStatus: WeeklySummaryCoachingStatus[],
  proxyCoachID?: string
): InvdividualMetricEntry[] => {
  const metricsMap = new Map(
    teamLHMetrics.map(single => {
      let values = single.userID.match(/\d+/g) as string[];
      return [values[0], single];
    })
  );

  const profileMap = constructProfileMap(profile, proxyCoachID);

  const coachingStatusMap = new Map(
    weeklySummaryCoachingStatus.map(status => [status.userID, status])
  );
  const individualEntries: InvdividualMetricEntry[] = [];

  profileMap.forEach((value, key) => {
    let coachingStatus = coachingStatusMap.get(key);
    const entry: InvdividualMetricEntry = {
      userId: value.relatedUser ? value.relatedUser.userID : value.userID,
      displayName: value.relatedUser
        ? value.relatedUser.displayName
        : value.displayName,
      alias: value.relatedUser ? value.relatedUser.alias : value.alias,
      lighthouseMetrics: metricsMap.get(key),
      isTm: value.isManager ? value.isManager : false,
      lastCoachedDate:
        coachingStatus && moment.utc(coachingStatus.lastCoachedDate).isValid()
          ? moment.utc(coachingStatus.lastCoachedDate)
          : undefined,
      lastCoachingStatus: coachingStatus?.lastCoachingStatus,
    };
    individualEntries.push(entry);
  });

  return individualEntries;
};

const coachingStatusLabels = (
  coachingStatus: DeliveredStatus
): React.ReactElement => {
  const statusLabel = DELIVERY_STATUS_LABEL[coachingStatus];
  if (statusLabel) {
    return (
      <FormattedMessage
        id={statusLabel.id}
        defaultMessage={statusLabel.default}
      />
    );
  }
  return <>--</>;
};

const displayLHTabelCell = (
  metricOption: string,
  report: InvdividualMetricEntry,
  index: number,
  nullArray: boolean[],
  firstOccurance: number,
  metrics?: LighthouseMetrics
) => {
  return (
    <TableCell key={metricOption + report.userId}>
      {metrics && metrics[metricOption as keyof LighthouseMetrics] ? (
        extractMetricValue(metricOption, metrics) === null ? (
          <>
            {nullArray[index] && index !== firstOccurance ? (
              <>
                <Text>--</Text>
                {DisplayBenchmark({
                  metricOption: metricOption,
                  metrics: metrics,
                })}
              </>
            ) : (
              <span style={{ paddingTop: '10px' }}>
                <Text>
                  <FormattedMessage
                    id='cs_perfportal_no_data'
                    defaultMessage='No data'
                  />
                </Text>
                {DisplayBenchmark({
                  metricOption: metricOption,
                  metrics: metrics,
                })}
              </span>
            )}
          </>
        ) : (metricOption as keyof LighthouseMetrics) === 'ACHT' ? (
          <>
            {extractMetricValue(metricOption, metrics)}{' '}
            <FormattedMessage id='cs_perfportal_min' defaultMessage='min' />
            {DisplayBenchmark({
              metricOption: metricOption,
              metrics: metrics,
            })}
          </>
        ) : (
          <>
            {parseStringInOrder(metricOption, report.lighthouseMetrics)}
            {DisplayBenchmark({
              metricOption: metricOption,
              metrics: metrics,
            })}
          </>
        )
      ) : (
        <span style={{ paddingTop: '10px' }}>
          <Text>
            <FormattedMessage
              id='cs_perfportal_no_data'
              defaultMessage='No data'
            />
          </Text>
        </span>
      )}
    </TableCell>
  );
};

const displayLHInOrder = (
  vertical: VerticalOption,
  OrderMap: Map<string, string[]>,
  report: InvdividualMetricEntry,
  achtEnabled: boolean,
  concessionEnabled: boolean,
  teamMetricTypes: Set<string>
) => {
  const map = vertical
    ? OrderMap.get(vertical)!.filter(
        metric =>
          !(!achtEnabled && metric === LighthouseMetricOption.ACHT) &&
          teamMetricTypes.has(metric)
      )
    : OrderMap.get(VerticalOption.Total)!.filter(
        metric => !(!achtEnabled && metric === LighthouseMetricOption.ACHT)
      );

  let isNullForTopLevelMetrics: boolean[] = [];
  map.forEach(metricIndex => {
    if (
      report.lighthouseMetrics !== undefined &&
      (report.lighthouseMetrics[
        metricIndex as keyof LighthouseMetrics
      ] as GeneralMetricType) !== null &&
      extractMetricValue(metricIndex, report.lighthouseMetrics) !== null
    ) {
      isNullForTopLevelMetrics.push(false);
    } else isNullForTopLevelMetrics.push(true);
  });

  const firstOccurance = isNullForTopLevelMetrics!.findIndex(x => x === true);

  return vertical
    ? OrderMap.get(vertical)!
        .filter(
          metric =>
            !(!achtEnabled && metric === LighthouseMetricOption.ACHT) &&
            !(
              CONSUMER_VERTICALS.has(vertical) &&
              !concessionEnabled &&
              metric === LighthouseMetricOption.CONCESSION_RATE
            ) &&
            teamMetricTypes.has(metric)
        )
        .map((metricOption, index) =>
          displayLHTabelCell(
            metricOption,
            report,
            index,
            isNullForTopLevelMetrics,
            firstOccurance,
            report.lighthouseMetrics
          )
        )
    : OrderMap.get(VerticalOption.Total)!
        .filter(
          metric => !(!achtEnabled && metric === LighthouseMetricOption.ACHT)
        )
        .map((metricOption, index) =>
          displayLHTabelCell(
            metricOption,
            report,
            index,
            isNullForTopLevelMetrics,
            firstOccurance,
            report.lighthouseMetrics
          )
        );
};

interface DirectReportsRowInput {
  report: InvdividualMetricEntry;
  week: string;
  team: TeamType;
  teamState: string;
  teamProfile: TeamProfile | Profile;
  teamProfileState: string;
  teamLHMetrics: LighthouseMetrics[];
  teamLHMetricsState: string;
  weeklySummaryCoachingStatus: WeeklySummaryCoachingStatus[];
  weeklySummaryCoachingStatusState: string;
  onClickControlled: Function;
  controlledOpen: boolean;
}

const generateDirectReportsRowInputForProxy = (
  reports: InvdividualMetricEntry[],
  week: string,
  profile: any,
  weeklySummaryCoachingStatus: WeeklySummaryCoachingStatus[],
  teamLHMetrics: LighthouseMetrics[]
): DirectReportsRowInput[] => {
  const directReportsRowInputs: DirectReportsRowInput[] = [];

  const teamCopy = new TeamType({
    keys: [TeamPermissions.coachingNotes],
    metrics: [],
    summaryMetrics: [],
  });

  reports.forEach(report => {
    const entry: DirectReportsRowInput = {
      report: report,
      week: week,
      team: teamCopy,
      teamState: DataState.LOADED,
      teamProfile: profile,
      teamProfileState: DataState.LOADED,
      teamLHMetrics: teamLHMetrics,
      teamLHMetricsState: DataState.LOADED,
      weeklySummaryCoachingStatus: weeklySummaryCoachingStatus,
      weeklySummaryCoachingStatusState: DataState.LOADED,
      onClickControlled: () => <></>,
      controlledOpen: false,
    };
    directReportsRowInputs.push(entry);
  });

  return directReportsRowInputs;
};

// attach action items to direct report. Will be used with on-click event on it.
// This is needed when direct report is a TM an we need to further see LM of all direct reports of that TM.
// Since we go only one level down in GM view, we will be using getProfile API, instead of getTeamProfile.
// getTeamProfile gets not just all direct reports, but also a flag isManager for each. This flag
// is not needed when
export const attachActionsToDirectReportsRows = (
  reports: InvdividualMetricEntry[],
  cscEntityPrefix: string,
  vertical: VerticalOption,
  channelValue: string,
  skillValue: string,
  segmentValue: string,
  marketplaceValue: string,
  week: string
): DirectReportsRowInput[] => {
  const directReportsRowInputs: DirectReportsRowInput[] = [];
  reports.forEach(report => {
    const apiNaws = DataSource.getDataFeed('NAWS');
    const [controlledOpen, setControlledOpen] = useState(false);
    const [team, setTeam] = useState<TeamType>(EmptyTeam);
    const [teamState, setTeamState] = useState(DataState.LOADING);
    const [teamProfile, setTeamProfile] =
      useState<TeamProfile>(EmptyTeamProfile);
    const [teamProfileState, setTeamProfileState] = useState(DataState.LOADING);
    const [teamLHMetrics, setTeamLHMetrics] = useState<LighthouseMetrics[]>([]);
    const [teamLHMetricsState, setTeamLHMetricsState] = useState(
      DataState.LOADING
    );
    const [weeklySummaryCoachingStatus, setWeeklySummaryCoachingStatus] =
      useState<WeeklySummaryCoachingStatus[]>([]);
    const [
      weeklySummaryCoachingStatusState,
      setWeeklySummaryCoachingStatusState,
    ] = useState(DataState.LOADING);

    const updateTeamWithProfile = (prof: TeamProfile) => {
      // fill the shared profile table
      const teamCopy = new TeamType({
        keys: [TeamPermissions.coachingNotes],
        metrics: [],
        summaryMetrics: [],
      });
      prof.reportingTeam.forEach(member => {
        const coachingNote = new CoachingNote(member.relatedUser.userID);
        const metric = new Metric({
          coachingNotes: [coachingNote],
          hmdGoal: EmptyHMDGoal,
        });
        teamCopy.metrics.push({
          user: member.relatedUser,
          metrics: metric,
          tm: member.isManager,
        });
      });
      setTeam(teamCopy);
    };

    const onClickControlled = useCallback(() => {
      setControlledOpen(!controlledOpen);
      if (controlledOpen) return;
      // If it is not TM or userId not yet loaded then no action with click
      if (!report.isTm || !report.userId) {
        return;
      }
      const profileQuery = apiNaws.prepareQuery(
        week || getCurrentWeek(),
        report.userId,
        cscEntityPrefix
      );

      apiNaws.getProfileData(
        profileQuery,
        (prof: Profile) => {
          const reportingTeam: ReportingTeamUser[] = [];

          prof.directReports.forEach(member => {
            const reportingTeamUser: ReportingTeamUser = {
              relatedUser: member,
              isManager: false, // We only go one level down in GM view.
            };
            reportingTeam.push(reportingTeamUser);
          });
          const teamProfile: TeamProfile = {
            user: prof.user,
            reportingTeam: reportingTeam,
          };
          setTeamProfile(teamProfile);
          updateTeamWithProfile(teamProfile);
        },
        (state: any) => {
          setTeamProfileState(state);
          setTeamState(state);
        }
      );

      apiNaws.getWeeklySummaryCoachingStatus(
        { userID: `${report.userId}` },
        setWeeklySummaryCoachingStatus,
        setWeeklySummaryCoachingStatusState
      );
    }, [controlledOpen, week]);

    useEffect(() => {
      if (!report.isTm || teamProfileState !== DataState.LOADED) {
        return;
      }
      const queryVariablesLM = apiNaws.prepareTeamLighthouseQuery(
        report.userId,
        cscEntityPrefix,
        vertical,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        week,
        week === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY,
        teamProfile.reportingTeam
      );
      apiNaws.getTeamLighthouseMetrics(
        queryVariablesLM,
        setTeamLHMetrics,
        setTeamLHMetricsState
      );
    }, [teamProfileState]);
    const entry: DirectReportsRowInput = {
      report: report,
      week: week,
      team: team,
      teamState: teamState,
      teamProfile: teamProfile,
      teamProfileState: teamProfileState,
      teamLHMetrics: teamLHMetrics,
      teamLHMetricsState: teamLHMetricsState,
      weeklySummaryCoachingStatus: weeklySummaryCoachingStatus,
      weeklySummaryCoachingStatusState: weeklySummaryCoachingStatusState,
      onClickControlled: onClickControlled,
      controlledOpen: controlledOpen,
    };
    directReportsRowInputs.push(entry);
  });
  return directReportsRowInputs;
};

const DirectReportsRow = (
  rowInput: DirectReportsRowInput,
  keys: Array<string>,
  vertical: VerticalOption,
  showWeeklyCoachingStatus: boolean,
  index: number,
  achtEnabled: boolean,
  concessionEnabled: boolean,
  sortParams: SortParams,
  manageCoachingNotes: boolean,
  sortingMethod: TeamTableSortMethod,
  teamMetricTypes: Set<string>,
  navigate: NavigateFunction
): React.ReactElement => {
  const isOdd = index % 2 === 1;
  const newCoachingEnabled = isFeatureEnabled(Feature.NEW_COACHING);

  const renderRow = (user: any) => (
    <>
      <TableCell>
        <Text>
          <Link
            data-csa-c-type='link'
            data-csa-c-slot-id='team-table-direct-reports-link-slot'
            data-csa-c-content-id={`team-table-direct-reports-link-for-${user.userId}`}
            data-csa-c-profile-id={`${user.userId}`}
            data-csa-c-selected-week={`${rowInput.week}`}
            href={renderUrl(`${urls.dashboard}/${user.userId}`)}
            onClick={navigate}
            type='secondary'
          >
            {`${user.displayName} (${user.alias})`}
          </Link>
        </Text>
      </TableCell>

      {displayLHInOrder(
        vertical,
        DisplayOrderForVertical,
        user,
        achtEnabled,
        concessionEnabled,
        teamMetricTypes
      )}

      {newCoachingEnabled && manageCoachingNotes && (
        <>
          <TableCell>
            {showWeeklyCoachingStatus &&
            user.lastCoachedDate &&
            moment.utc(user.lastCoachedDate).isValid()
              ? moment
                  .utc(user.lastCoachedDate)
                  .format('MMM. D, YYYY')
                  .toLocaleString()
              : '--'}
          </TableCell>
          <TableCell>
            {showWeeklyCoachingStatus && user.lastCoachingStatus !== undefined
              ? coachingStatusLabels(user.lastCoachingStatus)
              : '--'}
          </TableCell>
          <TableCell>
            <Text>
              <Link
                type='secondary'
                data-csa-c-type='link'
                data-csa-c-slot-id='team-direct-report-open-coaching-notes-link-slot'
                data-csa-c-content-id={`team-direct-report-open-coaching-notes-for-${user.userId}`}
                data-csa-c-profile-id={`${user.userId}`}
                data-csa-c-tag1='Team'
                data-csa-c-tag2='Performance'
                data-cy={`team-direct-report-open-coaching-notes-for-${user.userId}`}
                href={renderUrl(`${urls.coachingNotes}/${user.userId}`)}
                onClick={navigate}
              >
                <FormattedMessage
                  id='cs_perfportal_coaching_open'
                  defaultMessage='Open'
                />
              </Link>
            </Text>
          </TableCell>
        </>
      )}
    </>
  );

  return (
    <TableRow
      key={rowInput.report.userId}
      open={rowInput.controlledOpen}
      onClick={() => rowInput.onClickControlled()}
      backgroundColor={isOdd ? 'secondary' : 'primary'}
    >
      {renderRow(rowInput.report)}
      {rowInput.report.isTm && (
        <TableRow backgroundColor={isOdd ? 'secondary' : 'primary'}>
          <td
            style={{
              padding: '0 0 0 40px',
            }}
            colSpan={100}
          >
            <Text tag='span' type='b100'>
              <b>
                <FormattedMessage
                  id='cs_perfportal_direct_reports'
                  defaultMessage='Direct reports'
                />
              </b>
              <Divider />
            </Text>
          </td>
        </TableRow>
      )}
      {rowInput.report.isTm &&
        rowInput.teamLHMetricsState === DataState.LOADING && (
          <TableRow backgroundColor={isOdd ? 'secondary' : 'primary'}>
            <TableCell columnSpan={100}>
              <div style={{ textAlign: 'center' }}>
                <Loader type='circular' />
              </div>
            </TableCell>
          </TableRow>
        )}
      {rowInput.report.isTm &&
        rowInput.teamState === DataState.LOADED &&
        rowInput.teamProfileState === DataState.LOADED &&
        rowInput.teamLHMetricsState === DataState.LOADED &&
        parseIndividualValues(
          rowInput.teamLHMetrics,
          rowInput.teamProfile,
          rowInput.weeklySummaryCoachingStatus
        )
          .sort(
            sortingFunction(
              sortParams.sortColumn,
              sortParams.sortDirection,
              sortingMethod
            )
          )
          .map((user: any) => (
            <TableRow
              backgroundColor={isOdd ? 'secondary' : 'primary'}
              key={user.userId}
            >
              {renderRow(user)}
            </TableRow>
          ))}
      {rowInput.report.isTm &&
        (rowInput.teamState === DataState.ERROR ||
          (rowInput.teamState === DataState.LOADED &&
            parseValues(
              rowInput.team.metrics,
              rowInput.weeklySummaryCoachingStatus
            ).length === 0)) && (
          <TableRow backgroundColor={isOdd ? 'secondary' : 'primary'}>
            <TableCell columnSpan={100}>
              <Text>
                <FormattedMessage
                  id='cs_perfportal_no_associates_found'
                  defaultMessage='No associates found.'
                />
              </Text>
            </TableCell>
          </TableRow>
        )}
    </TableRow>
  );
};

const DirectReports: React.FC<DirectReportsProps> = ({
  cscEntityPrefix,
  vertical,
  channelValue,
  skillValue,
  segmentValue,
  marketplaceValue,
  keys,
  week,
  profile,
  weeklySummaryCoachingStatus,
  showWeeklyCoachingStatus,
  teamLHMetrics,
  achtEnabled,
  concessionEnabled,
  manageCoachingNotes,
  proxyCoachID,
  sortingMethod,
  sourcePage,
  teamMetricTypes,
}: DirectReportsProps): React.ReactElement => {
  const navigate = useNavigate();

  const reports = parseIndividualValues(
    teamLHMetrics,
    profile,
    weeklySummaryCoachingStatus,
    proxyCoachID
  );
  const [sortColumn, setSortColumn] = useState('displayName');
  const [sortDirection, setSortDirection] = useState('ascending');
  reports.sort(sortingFunction(sortColumn, sortDirection, sortingMethod));
  const attachedDirectReportsRows = proxyCoachID
    ? generateDirectReportsRowInputForProxy(
        reports,
        week,
        profile,
        weeklySummaryCoachingStatus,
        teamLHMetrics
      )
    : attachActionsToDirectReportsRows(
        reports,
        cscEntityPrefix,
        vertical,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        week
      );
  const widthChange =
    CONSUMER_VERTICALS.has(vertical) || D2_VERTICALS.has(vertical)
      ? '8.5%'
      : '10.2%';
  const newCoachingEnabled = isFeatureEnabled(Feature.NEW_COACHING);
  // eslint-disable-next-line no-shadow

  const onSort = useCallback(
    ({
      sortColumn,
      sortDirection,
    }: {
      sortColumn: string;
      sortDirection: string;
    }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    []
  );

  const [TableSortTrackingTag, tableSortClick] = useCustomTracking(
    `team-direct-report-table-benchmark-sort`,
    `team-direct-report-table-benchmark-sort`,
    {
      'data-csa-c-tag1': sourcePage,
      'data-csa-c-content-id': 'sortby_metric',
      'data-csa-c-metric': isMetricRelatedColumn(sortColumn)
        ? extractMetricOptionFromColumn(sortColumn)
        : '',
      'data-csa-c-sort_method': sortingMethod ?? '',
      'data-csa-c-sort_direction': sortDirection ?? '',
    }
  );

  useEffect(() => {
    if (isMetricRelatedColumn(sortColumn)) {
      tableSortClick();
    }
  }, [sortColumn, sortDirection]);

  const entryCount = attachedDirectReportsRows.length;
  const itemsPerPage = DEFAULT_ITEM_COUNT_PER_PAGE;
  const numberOfPages = Math.ceil(entryCount / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(1);
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;

  return attachedDirectReportsRows.length === 0 ? (
    <Text>
      <FormattedMessage
        id='cs_perfportal_no_associates_found'
        defaultMessage='No associates found.'
      />
    </Text>
  ) : (
    <Column width='100%' alignmentHorizontal='center' spacing='none'>
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={
          sortDirection === 'ascending' ? 'ascending' : 'descending'
        }
        onSort={onSort}
        showDividers
      >
        <TableSortTrackingTag />
        <TableRow highlightOnHover={false}>
          <TableCell
            sortColumn='displayName'
            width='19%'
            alignmentVertical='bottom'
          >
            <FormattedMessage
              id='cs_perfportal_associate'
              defaultMessage='Associate'
            />
          </TableCell>

          {
            // Display the metrics in nature order
            vertical &&
              DisplayOrderForVertical.get(vertical)!
                .filter(
                  metric =>
                    !(!achtEnabled && metric === LighthouseMetricOption.ACHT) &&
                    !(
                      CONSUMER_VERTICALS.has(vertical) &&
                      !concessionEnabled &&
                      metric === LighthouseMetricOption.CONCESSION_RATE
                    ) &&
                    teamMetricTypes.has(metric)
                )
                .map(metric => (
                  <TableCell
                    sortColumn={LIGHTHOUSE_METRICS + metric}
                    width={widthChange}
                    key={metric + 'HEADER'}
                    alignmentVertical='bottom'
                  >
                    <FormattedMessage
                      {...getMetricStringIdAndDefaultMessage(metric)}
                    />
                  </TableCell>
                ))
          }

          {
            // Display the metrics in nature order
            !vertical &&
              DisplayOrderForVertical.get(VerticalOption.Total)!
                .filter(
                  metric =>
                    !(!achtEnabled && metric === LighthouseMetricOption.ACHT)
                )
                .map(metric => (
                  <TableCell
                    sortColumn={LIGHTHOUSE_METRICS + metric}
                    width={widthChange}
                    key={metric + 'HEADER'}
                    alignmentVertical='bottom'
                  >
                    <FormattedMessage
                      {...getMetricStringIdAndDefaultMessage(metric)}
                    />
                  </TableCell>
                ))
          }

          {newCoachingEnabled && manageCoachingNotes && (
            <TableCell
              sortColumn='lastCoachedDate'
              width='10%'
              alignmentVertical='bottom'
            >
              <FormattedMessage
                id='cs_perfportal_last_coached'
                defaultMessage='Last Coached'
              />
            </TableCell>
          )}
          {newCoachingEnabled && manageCoachingNotes && (
            <TableCell width='10%' alignmentVertical='bottom'>
              <FormattedMessage
                id='cs_perfportal_coaching_notes'
                defaultMessage='Coaching notes'
              />
            </TableCell>
          )}
          {newCoachingEnabled && manageCoachingNotes && (
            <TableCell width='10%' />
          )}
        </TableRow>
        {attachedDirectReportsRows
          .slice(firstVisibleIndex, lastVisibleIndex)
          .map((rowInput: DirectReportsRowInput, index: number) =>
            DirectReportsRow(
              rowInput,
              keys,
              vertical,
              showWeeklyCoachingStatus,
              index,
              achtEnabled,
              concessionEnabled,
              {
                sortColumn,
                sortDirection,
              },
              manageCoachingNotes,
              sortingMethod,
              teamMetricTypes,
              navigate
            )
          )}
      </Table>
      {entryCount > itemsPerPage ? (
        <Pagination
          showSkipArrows
          numberOfPages={numberOfPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      ) : null}
    </Column>
  );
};

export default DirectReports;
